import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { formatEther, formatUnits, maxUint256, getContract } from "viem";
import {
  privatekeyArray,
  chainId,
  token,
  spenToken,
  router,
  rpc,
  pairToken,
  permit2
} from "@/store";
import { Notification } from "@/components";
import { addressHand, isBNBHandler, isMainAddress } from "../utils";
import {
  WALLETTOOL,
  DEAD,
  ALLOWANCE,
  MainTokenConfig,
  zeroAddress,
  WalletToolAddressABI
} from "@/config";
import { useProviderInstance } from '@/context/ProviderContext';
import { privateKeyToAccount } from 'viem/accounts';
import { to0xString } from "@/utils/toHex";

export interface WalletInfoType {
  BNBb: bigint[];
  SPENTOKENb: bigint[];
  TOKENb: bigint[];
}


const useWalletInfo = () => {
  const provider = useProviderInstance();
  const chainId_ = useSelector(chainId);
  const pricateKeyArr = useSelector(privatekeyArray);
  const _token = useSelector(token);
  const _spenToken = useSelector(spenToken);
  const _router = useSelector(router);

  // 刷单钱包地址
  const [walletAddrArr, setWalletAddrArr] = useState<string[]>([]);
  // 刷单钱包地址 余额
  const [walletConfig, setWalletConfig] = useState({
    BNBbArr: [] as string[],
    SPENTOKENbArr: [] as string[],
    TOKENbArr: [] as string[],
  });
  const infoRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    getWalletAddrArr();
  }, [pricateKeyArr, provider]);

  const getWalletAddrArr = () => {
    if (pricateKeyArr.length === 0) {
      setWalletConfig({ BNBbArr: [], SPENTOKENbArr: [], TOKENbArr: [] });
      setWalletAddrArr([]);
      return;
    }
    if (pricateKeyArr.length <= 0 || !provider) return;
    try {
      const arr: string[] = [];
      pricateKeyArr.forEach((key: string) => {
        const account = privateKeyToAccount(to0xString(key));
        arr.push(account.address);
      });
      setWalletAddrArr(arr);
    } catch (error) {
      Notification.Error(`钱包加载错误`);
    }
  };

  useEffect(() => {
    if (walletAddrArr.length > 0 && _spenToken?.address) {
      getWalletInfo();
      getAllowance();

    }
  }, [walletAddrArr.length, _token.address, _spenToken.address, provider]);

  const getWalletInfo = async () => {
    if (walletAddrArr.length === 0) {
      setWalletConfig({ BNBbArr: [], SPENTOKENbArr: [], TOKENbArr: [] });
      setWalletAddrArr([]);
      if (infoRef.current) {
        clearInterval(infoRef.current);
        infoRef.current = null;
      }
      return;
    }
    if (provider.chain.id !== chainId_) return;

    try {
      // 获取合约实例 - 使用 as any 或类型断言绕过类型检查
      const toolContract = getContract({
        address: WALLETTOOL[chainId_] as `0x${string}`,
        abi: WalletToolAddressABI,
        client: provider,
      }) as any;

      const token = _token?.address || MainTokenConfig[chainId_][0].address;

      let isHoveToken = true;
      let isHoveSpendToken = _spenToken?.address ? true : false;

      let spendToken: string;
      if (_router.label.includes("V4")) {
        spendToken =
          _spenToken?.address === zeroAddress
            ? MainTokenConfig[chainId_][0].address
            : _spenToken?.address;
      } else {
        spendToken = _spenToken?.address ?? DEAD;
      }

      let isSpendBNB = false;
      if (
        isMainAddress(_spenToken?.address) ||
        _spenToken?.address === zeroAddress
      ) {
        isSpendBNB = true;
      }

      if (isSpendBNB) isHoveSpendToken = false;

      // 调用合约方法 - 使用 read 或直接调用
      const info = await toolContract.read.getWalletInfo([
        walletAddrArr,
        spendToken as `0x${string}`,
        token as `0x${string}`,
        isHoveSpendToken,
        isHoveToken,
      ]) as WalletInfoType;

      const BNBbArr: string[] = [];
      let SPENTOKENbArr: string[] = [];
      const TOKENbArr: string[] = [];

      info[0].forEach((item: bigint) => {
        const amount = item ? formatEther(item) : "0";
        BNBbArr.push(amount);
      });

      info[1].forEach((item: bigint) => {
        const amount = item
          ? formatUnits(item, _spenToken.decimals)
          : "0";
        SPENTOKENbArr.push(amount);
      });

      info[2].forEach((item: bigint) => {
        const amount = item
          ? formatUnits(item, _token.decimals)
          : "0";
        TOKENbArr.push(amount);
      });

      if (isSpendBNB) SPENTOKENbArr = BNBbArr;

      setWalletConfig({ BNBbArr, SPENTOKENbArr, TOKENbArr });
      return true;
    } catch (error) {
      console.log(error, "获取余额失败");
      Notification.Error(String(error));
      setWalletConfig({
        BNBbArr: walletAddrArr.map(() => "0"),
        SPENTOKENbArr: walletAddrArr.map(() => "0"),
        TOKENbArr: walletAddrArr.map(() => "0"),
      });
      return false;
    }
  };

  // 统计
  const [totalConfig, setTotalConfig] = useState({
    bnbTotal: "",
    usdtTotal: "",
    tokenTotal: "",
  });

  useEffect(() => {
    getTotal();
  }, [walletConfig]);

  const getTotal = () => {
    let bnbTo = 0;
    let usdtTo = 0;
    let tokenTo = 0;

    walletConfig.BNBbArr.forEach((item) => {
      bnbTo += Number(item);
    });
    walletConfig.SPENTOKENbArr.forEach((item) => {
      usdtTo += Number(item);
    });
    walletConfig.TOKENbArr.forEach((item) => {
      tokenTo += Number(item);
    });

    const total = { bnbTotal: "", usdtTotal: "", tokenTotal: "" };
    total.bnbTotal = bnbTo.toFixed(5).toString();
    total.usdtTotal = usdtTo.toFixed(5).toString();
    total.tokenTotal = tokenTo.toFixed(5).toString();

    setTotalConfig(total);
  };

  const [tokenIsA, setTokenIsA] = useState<boolean[]>([]);
  const _permit2 = useSelector(permit2);

  const [spenTokenIsA, setSpenTokenIsA] = useState<boolean[]>([]);

  const getAllowance = async () => {
    try {
      const _tokenIsA: boolean[] = [];
      const _spenTokenIsA: boolean[] = [];

      if (ALLOWANCE[chainId_]) {
        const allowanceContract = getContract({
          address: ALLOWANCE[chainId_] as `0x${string}`,
          abi: ALLOWANCE.ABI,
          client: provider,
        }) as any;


        const result = await allowanceContract.read.allowChecks([
          walletAddrArr,
          [_token?.address, _spenToken?.address],
          _permit2,
        ]) as [bigint[], bigint[]];

        const amount = maxUint256 / BigInt(10);

        result[0].forEach((item: bigint) => {
          _tokenIsA.push(amount > item ? false : true);
        });

        result[1].forEach((item: bigint) => {
          _spenTokenIsA.push(amount > item ? false : true);
        });
      }

      setTokenIsA(_tokenIsA);
      setSpenTokenIsA(_spenTokenIsA);
    } catch (error) {
      console.log(error);
    }
  };

  const [newKey, setNewKey] = useState<string[]>([]);

  useEffect(() => {
    getNewKey();
  }, [walletConfig, tokenIsA]);

  const getNewKey = async () => {
    try {
      const _arr: string[] = [];
      walletConfig.BNBbArr.forEach((item, index) => {
        if (Number(item) > 0.001) {
          if (Number(walletConfig.TOKENbArr[index]) > 0) {
            if (tokenIsA[index]) {
              _arr.push(pricateKeyArr[index]);
            }
          }
        }
      });
      setNewKey(_arr);
    } catch (error) {
      console.log(error);
    }
  };

  return {
    walletAddrArr,
    walletConfig,
    totalConfig,
    getWalletInfo,
    getAllowance,
    tokenIsA,
    spenTokenIsA,
    newKey,
  };
};

export default useWalletInfo;